import { GameManager } from '../game';

import { CameraManager } from './CameraManager';

/** Options for {@link CameraJitter.start} and amplitude updates. */
export interface CameraJitterOptions {
  /** Peak screen-pixel offset on each axis (random in [-amp, +amp] per frame). */
  amplitudePx: number;
}

const DEFAULT_AMPLITUDE_PX = 2;

/**
 * Light screen-space camera shake for cinematics.
 * Offsets a parent of the viewport so follow / animate / zoom keep working.
 * Callers own start, amplitude changes, and stop (not letterbox).
 */
export class CameraJitter {
  private static _running = false;
  private static _amplitudePx = 0;

  public static get isRunning(): boolean {
    return CameraJitter._running;
  }

  public static get amplitudePx(): number {
    return CameraJitter._amplitudePx;
  }

  /**
   * Begins per-frame shake. Idempotent while already running; use {@link setAmplitude} to raise/lower.
   */
  public static start(options?: Partial<CameraJitterOptions>): void {
    CameraJitter._amplitudePx = Math.max(0, options?.amplitudePx ?? DEFAULT_AMPLITUDE_PX);

    if (CameraJitter._running) {
      return;
    }

    CameraJitter._running = true;
    GameManager.app.ticker.add(CameraJitter.tick);
  }

  /** Changes shake strength without restarting the ticker. */
  public static setAmplitude(amplitudePx: number): void {
    CameraJitter._amplitudePx = Math.max(0, amplitudePx);
  }

  /** Stops the ticker and clears the screen offset. */
  public static stop(): void {
    if (!CameraJitter._running) {
      CameraManager.setJitterOffset(0, 0);
      CameraJitter._amplitudePx = 0;
      return;
    }

    CameraJitter._running = false;
    CameraJitter._amplitudePx = 0;
    GameManager.app.ticker.remove(CameraJitter.tick);
    CameraManager.setJitterOffset(0, 0);
  }

  private static tick = (): void => {
    const amp = CameraJitter._amplitudePx;
    if (amp <= 0) {
      CameraManager.setJitterOffset(0, 0);
      return;
    }

    CameraManager.setJitterOffset(
      (Math.random() - 0.5) * 2 * amp,
      (Math.random() - 0.5) * 2 * amp
    );
  };
}
